// Media library schemas — the admin R2 upload flow. docs/next_session.md
// "content depth" / CLAUDE.md §11.
//
// Uploads go through the API Worker (multipart POST → env.MEDIA.put), not a
// presigned URL, so no R2 access keys are needed this pass. The Worker validates
// kind/mime/size server-side. `media_uploads_pending` + presigned URLs are a
// later optimisation for large files.

import { z } from 'zod';

export const mediaKind = z.enum(['image', 'audio', 'video', 'pdf']);
export type MediaKind = z.infer<typeof mediaKind>;

// Allowed MIME types + a max size (bytes) per kind. The Worker rejects anything
// outside these — a cheap first-pass content gate (CLAUDE.md §11/§29). Re-encode
// to WebP + a real scan land at integration time; noted as a TODO in the route.
export const MEDIA_LIMITS: Record<
  MediaKind,
  { mimes: readonly string[]; maxBytes: number; exts: Record<string, string> }
> = {
  image: {
    mimes: ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'],
    maxBytes: 8 * 1024 * 1024,
    exts: {
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'image/webp': 'webp',
      'image/gif': 'gif',
      'image/svg+xml': 'svg',
    },
  },
  audio: {
    mimes: ['audio/mpeg', 'audio/mp4', 'audio/ogg', 'audio/wav', 'audio/webm'],
    maxBytes: 20 * 1024 * 1024,
    exts: {
      'audio/mpeg': 'mp3',
      'audio/mp4': 'm4a',
      'audio/ogg': 'ogg',
      'audio/wav': 'wav',
      'audio/webm': 'weba',
    },
  },
  video: {
    mimes: ['video/mp4', 'video/webm'],
    maxBytes: 200 * 1024 * 1024,
    exts: { 'video/mp4': 'mp4', 'video/webm': 'webm' },
  },
  pdf: {
    mimes: ['application/pdf'],
    maxBytes: 20 * 1024 * 1024,
    exts: { 'application/pdf': 'pdf' },
  },
};

// The multipart POST /admin/media form carries the file plus these fields. We
// validate them here after pulling them out of the form (the file itself is a
// Blob handled in the route).
export const mediaUploadFields = z.object({
  kind: mediaKind,
  altKey: z.string().trim().max(240).optional(),
  license: z.string().trim().max(500).optional(),
});
export type MediaUploadFields = z.infer<typeof mediaUploadFields>;

export const mediaUpdateInput = z.object({
  altKey: z.string().trim().max(240).nullable().optional(),
  license: z.string().trim().max(500).nullable().optional(),
});
export type MediaUpdateInput = z.infer<typeof mediaUpdateInput>;

export const mediaListQuery = z.object({
  kind: mediaKind.optional(),
  q: z.string().trim().max(200).optional(),
});
export type MediaListQuery = z.infer<typeof mediaListQuery>;

// Response DTO — one media asset. `r2Key` is the relative key an activity
// payload's mediaRef stores; the CDN base is prefixed client-side.
export const mediaDto = z.object({
  id: z.uuid(),
  kind: mediaKind,
  r2Key: z.string(),
  mime: z.string(),
  sizeBytes: z.number().int().nullable(),
  durationMs: z.number().int().nullable(),
  widthPx: z.number().int().nullable(),
  heightPx: z.number().int().nullable(),
  altKey: z.string().nullable(),
  license: z.string().nullable(),
  createdAt: z.string(),
});
export type MediaDto = z.infer<typeof mediaDto>;
